/**
 * The user's Benny app flags (`bennyEnabled`, `bennyCompanionEnabled`,
 * `bennyNotificationsEnabled`) with one optimistic save shared by the
 * settings page and the onboarding step, so "turn Benny on" is the same
 * write wherever it happens.
 */
import type {
  CompanionPreferenceChangedPayload,
  CompanionView,
} from '@/lib/benny-companion/events'
import {
  getCompanionPersonality,
  getCompanionQuietUntil,
  normalizeCompanionPreferenceChange,
} from '@/lib/benny-companion/events'
import { useAuth } from '@/store/auth.store'
import { useOrganizationStore } from '@/store/organization.store'
import {
  getUserAppPreference,
  useUserPreferencesStore,
} from '@/store/user-preferences.store'
import { toast } from 'sonner'

import type { AppPreferences, UserPreferenceData } from '@scrambl/shared'
import { PreferenceContextEnum, PreferenceScopeEnum } from '@scrambl/shared'

export type BennyAppFlag =
  | 'bennyEnabled'
  | 'bennyCompanionEnabled'
  | 'bennyNotificationsEnabled'

let preferenceSaveQueue: Promise<unknown> = Promise.resolve()

/** Serialize partial Benny updates so quick changes preserve each other. */
function saveBennyPreferencePatch(
  patch: Partial<AppPreferences>,
  optimistic = false,
): Promise<boolean> {
  const ownerId = useAuth.getState().user?.id
  const organizationId = useOrganizationStore.getState().currentOrganization?.id
  const isCurrentOwner = () =>
    useAuth.getState().user?.id === ownerId &&
    useOrganizationStore.getState().currentOrganization?.id === organizationId

  const save = async () => {
    const store = useUserPreferencesStore.getState()
    if (!ownerId || !isCurrentOwner()) return false
    const currentPrefs = store.preferences
    const currentAppPref = getUserAppPreference(currentPrefs)
    if (currentAppPref?.userId && currentAppPref.userId !== ownerId)
      return false
    const existing = (currentAppPref?.preferences ?? {}) as AppPreferences
    const updatedPrefs = currentPrefs.filter(
      p =>
        !(
          p.context === PreferenceContextEnum.APP &&
          p.scope === PreferenceScopeEnum.USER &&
          (p.moduleType === null || p.moduleType === undefined)
        ),
    )
    updatedPrefs.push({
      ...(currentAppPref ?? {
        context: PreferenceContextEnum.APP,
        scope: PreferenceScopeEnum.USER,
        moduleType: null,
        userId: ownerId,
      }),
      preferences: { ...existing, ...patch },
    } as unknown as UserPreferenceData)
    if (optimistic)
      useUserPreferencesStore.setState({ preferences: updatedPrefs })

    try {
      const saved = await store.savePreference(
        PreferenceContextEnum.APP,
        { ...existing, ...patch },
        undefined,
        PreferenceScopeEnum.USER,
      )
      if (!saved) throw new Error('Preference was not saved')
      return isCurrentOwner()
    } catch {
      if (
        optimistic &&
        isCurrentOwner() &&
        useUserPreferencesStore.getState().preferences === updatedPrefs
      )
        useUserPreferencesStore.setState({ preferences: currentPrefs })
      return false
    }
  }
  const result = preferenceSaveQueue.then(save, save)
  preferenceSaveQueue = result
  return result
}

/**
 * Optimistically flip one Benny flag in the preferences store, then persist
 * it; reverts (and reports) on failure. Resolves true when the save landed.
 */
export async function saveBennyAppFlag(
  key: BennyAppFlag,
  checked: boolean,
  messages?: { on: string; off: string },
): Promise<boolean> {
  const saved = await saveBennyPreferencePatch({ [key]: checked }, true)
  if (messages) {
    if (saved) toast.success(checked ? messages.on : messages.off)
    else toast.error("Couldn't update the setting.")
  }
  return saved
}

export function saveBennyCompanionPreferences(
  change: CompanionPreferenceChangedPayload,
): Promise<boolean> {
  const normalized = normalizeCompanionPreferenceChange(change)
  if (!normalized || !useUserPreferencesStore.getState().isInitialized) {
    return Promise.resolve(false)
  }
  const patch: Partial<AppPreferences> = {}
  if (normalized.personality !== undefined) {
    patch.bennyCompanionPersonality = normalized.personality
  }
  if ('quietUntil' in normalized) {
    patch.bennyCompanionQuietUntil = normalized.quietUntil
  }
  return saveBennyPreferencePatch(patch)
}

export function saveBennyCompanionView(view: CompanionView): Promise<boolean> {
  return saveBennyPreferencePatch({ bennyCompanionView: view })
}

export function useBennyPreference() {
  const preferences = useUserPreferencesStore(s => s.preferences)
  const isInitialized = useUserPreferencesStore(s => s.isInitialized)
  const appPref = getUserAppPreference(preferences)
  const flags = (appPref?.preferences ?? {}) as AppPreferences
  return {
    isInitialized,
    bennyEnabled: flags.bennyEnabled ?? false,
    bennyNotificationsEnabled: flags.bennyNotificationsEnabled ?? false,
    personality: getCompanionPersonality(flags.bennyCompanionPersonality),
    quietUntil: getCompanionQuietUntil(flags.bennyCompanionQuietUntil),
    saveAppFlag: saveBennyAppFlag,
    saveCompanionPreferences: saveBennyCompanionPreferences,
  }
}
